// Spin the Bottle furniture: a round rug (~3.5 m), twelve floor cushions
// around it and a small sign. Built from primitives so it loads instantly;
// to swap in a Blender GLB later, render it inside <RugSetGLB> the way
// CardTableModel.tsx does (Suspense + error boundary, primitive fallback).
// Decoration only: nothing here is collidable. Floor origin, metres.
//
// The bottle itself is drawn live by BottleTable3D (it spins).

import React from 'react';
import { Text } from '@react-three/drei';
import { noRaycast } from '../../VR/vrUi';
import { BOTTLE_CHAIRS, CUSHION_TOP, RUG_R, SIGN_R } from './bottleLayout';

const RUG = '#3c2a5a', RUG_RING = '#e0b44c', RUG_INNER = '#563d80';
const CUSHION_COLORS = ['#e2574c', '#f2a03d', '#f5d547', '#5cc189', '#3fb6c6', '#5a8ee6', '#9a6be0', '#e266b3'];
const nc = { collidable: false };

export function BottleRug() {
  return (
    <group userData={nc}>
      <mesh position={[0, 0.006, 0]} receiveShadow userData={nc}>
        <cylinderGeometry args={[RUG_R, RUG_R, 0.012, 64]} />
        <meshStandardMaterial color={RUG} roughness={0.95} />
      </mesh>
      {/* Gold border + an inner circle for the bottle */}
      <mesh position={[0, 0.013, 0]} rotation={[-Math.PI / 2, 0, 0]} userData={nc}>
        <ringGeometry args={[RUG_R - 0.09, RUG_R - 0.04, 64]} />
        <meshStandardMaterial color={RUG_RING} roughness={0.6} metalness={0.2} />
      </mesh>
      <mesh position={[0, 0.013, 0]} rotation={[-Math.PI / 2, 0, 0]} userData={nc}>
        <circleGeometry args={[0.62, 48]} />
        <meshStandardMaterial color={RUG_INNER} roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.014, 0]} rotation={[-Math.PI / 2, 0, 0]} userData={nc}>
        <ringGeometry args={[0.6, 0.63, 48]} />
        <meshStandardMaterial color={RUG_RING} roughness={0.6} metalness={0.2} />
      </mesh>
    </group>
  );
}

/** One round floor cushion (top at CUSHION_TOP). */
export function FloorCushion({ color }: { color: string }) {
  return (
    <group userData={nc}>
      <mesh position={[0, CUSHION_TOP / 2, 0]} castShadow receiveShadow userData={nc}>
        <cylinderGeometry args={[0.25, 0.27, CUSHION_TOP, 24]} />
        <meshStandardMaterial color={color} roughness={0.85} />
      </mesh>
      <mesh position={[0, CUSHION_TOP - 0.004, 0]} rotation={[Math.PI / 2, 0, 0]} userData={nc}>
        <torusGeometry args={[0.245, 0.018, 8, 28]} />
        <meshStandardMaterial color={color} roughness={0.7} />
      </mesh>
      {/* Button in the middle */}
      <mesh position={[0, CUSHION_TOP + 0.008, 0]} userData={nc}>
        <sphereGeometry args={[0.022, 10, 8]} />
        <meshStandardMaterial color="#fff6e0" roughness={0.6} />
      </mesh>
    </group>
  );
}

/** A small standing sign just off the rug. */
export function BottleSign() {
  // Between cushions 1 and 2 (45°), facing the middle.
  const a = Math.PI / 4 + Math.PI / 12;
  const x = Math.sin(a) * SIGN_R, z = -Math.cos(a) * SIGN_R;
  return (
    <group position={[x, 0, z]} rotation={[0, Math.atan2(-x, -z), 0]} userData={nc}>
      <mesh position={[0, 0.45, 0]} castShadow userData={nc}>
        <cylinderGeometry args={[0.025, 0.03, 0.9, 10]} />
        <meshStandardMaterial color="#5b3b22" roughness={0.8} />
      </mesh>
      <mesh position={[0, 0.98, 0]} castShadow userData={nc}>
        <boxGeometry args={[0.7, 0.32, 0.04]} />
        <meshStandardMaterial color="#2a1d40" roughness={0.7} />
      </mesh>
      {/* Readable from inside the circle (+Z faces the middle) and from outside */}
      {[0, Math.PI].map((ry) => (
        <group key={ry} rotation={[0, ry, 0]}>
          <Text raycast={noRaycast} position={[0, 1.02, 0.022]} fontSize={0.075} color="#f5d547" anchorX="center" anchorY="middle">Truth or Dare</Text>
          <Text raycast={noRaycast} position={[0, 0.93, 0.022]} fontSize={0.045} color="#f2e9ff" anchorX="center" anchorY="middle">sit on a cushion to play</Text>
        </group>
      ))}
    </group>
  );
}

/** The whole set: rug + 12 cushions + sign (registry `Furniture`). */
export function BottleRugSet() {
  return (
    <group userData={nc}>
      <BottleRug />
      {BOTTLE_CHAIRS.map((c, i) => (
        <group key={i} position={[c.x, 0, c.z]} rotation={[0, c.yaw, 0]}>
          <FloorCushion color={CUSHION_COLORS[i % CUSHION_COLORS.length]} />
        </group>
      ))}
      <BottleSign />
    </group>
  );
}

export default BottleRugSet;
